'use client';
import type { ColorScheme as ColorSchemeTypes } from "@/sanity.types";

import { type CSSProperties, ReactNode, useEffect } from "react";

type Props = {
  colorScheme?: ColorSchemeTypes | 'charcoal' | null;
  children: ReactNode;
  isGlobal?: boolean;
}

interface CustomCSSProperties extends CSSProperties {
  '--color-background'?: string;
  '--color-foreground'?: string;
}

export default function ColorScheme(props: Props) {
  const { colorScheme, children, isGlobal } = props;

  let colorBackground: string = 'var(--color-background)';
  let colorForeground: string = 'var(--color-foreground)';

  switch (colorScheme) {
    case 'pea':
      colorBackground = 'var(--color-pea)';
      colorForeground = 'var(--color-sky)';
      break;

    case 'sky':
      colorBackground = 'var(--color-sky)';
      colorForeground = 'var(--color-pea)';
      break;

    case 'blueberry':
      colorBackground = 'var(--color-blueberry)';
      colorForeground = 'var(--color-grape)';
      break;

    case 'grape':
      colorBackground = 'var(--color-grape)';
      colorForeground = 'var(--color-blueberry)';
      break;

    case 'plum':
      colorBackground = 'var(--color-plum)';
      colorForeground = 'var(--color-tangerine)';
      break;

    case 'tangerine':
      colorBackground = 'var(--color-tangerine)';
      colorForeground = 'var(--color-plum)';
      break;

    case 'royal':
      colorBackground = 'var(--color-royal)';
      colorForeground = 'var(--color-lilac)';
      break;

    case 'lilac':
      colorBackground = 'var(--color-lilac)';
      colorForeground = 'var(--color-royal)';
      break;

    case 'sun':
      colorBackground = 'var(--color-sun)';
      colorForeground = 'var(--color-sky)';
      break;

    case 'pea-light':
      colorBackground = 'var(--color-pea-light)';
      colorForeground = 'var(--color-soft-black)';
      break;

    case 'royal-light':
      colorBackground = 'var(--color-royal-light)';
      colorForeground = 'var(--color-soft-black)';
      break;

    case 'charcoal':
      colorBackground = 'var(--color-greyscale-charcoal)';
      colorForeground = 'var(--color-greyscale-vanilla)';
      break;

    default:
      colorBackground = 'var(--color-beige)';
      colorForeground = 'var(--color-soft-black)';
      break;
  }

  const style: CustomCSSProperties = {
    '--color-background': colorBackground,
    '--color-foreground': colorForeground,
    display: 'contents',
  }

  useEffect(() => {
    if (!isGlobal) return;

    if (colorBackground) {
      document.documentElement.style.setProperty('--color-background', colorBackground);
    }

    if (colorForeground) {
      document.documentElement.style.setProperty('--color-foreground', colorForeground);
    }

    return () => {
      document.documentElement.style.setProperty('--color-background', 'var(--color-beige)');
      document.documentElement.style.setProperty('--color-foreground', 'var(--color-soft-black)');
    }
  }, [colorBackground, colorForeground, isGlobal]);

  return (
    <div style={style}>
      {children}
    </div>
  )
}
